import { useRef, useState } from "react";
import { sendPasswordResetEmail, signInWithEmailAndPassword, signOut } from "firebase/auth";
import { auth } from "../../firebase/auth";
import { db } from "../../firebase/firestore";
import { doc, getDoc, serverTimestamp, setDoc } from "firebase/firestore";
import { useNavigate } from "react-router-dom";
import "../../Css/Login.css";
import { redirectUser } from "../../utils/redirectUser";
import { retryAsync } from "../../utils/errorHandler";
import {
    checkFirebaseAuthReachability,
    getFirebaseAuthTroubleshootingHint,
} from "../../utils/firebaseConnection";
import logo from "../../assets/logo.png";
import heroImage from "../../assets/hero.png";

const LOGIN_ATTEMPTS_KEY = "login_attempts_v1";
const MAX_FAILED_ATTEMPTS = 5;
const LOCKOUT_WINDOW_MS = 10 * 60 * 1000;

function getAttemptMap() {
    try {
        const raw = localStorage.getItem(LOGIN_ATTEMPTS_KEY);
        return raw ? JSON.parse(raw) : {};
    } catch {
        return {};
    }
}

function saveAttemptMap(map) {
    localStorage.setItem(LOGIN_ATTEMPTS_KEY, JSON.stringify(map));
}

function clearFailedAttempts(email) {
    const map = getAttemptMap();
    delete map[email];
    saveAttemptMap(map);
}

function registerFailedAttempt(email) {
    const map = getAttemptMap();
    const current = map[email] || { count: 0, firstFailedAt: Date.now() };
    const now = Date.now();

    if (now - current.firstFailedAt > LOCKOUT_WINDOW_MS) {
        map[email] = { count: 1, firstFailedAt: now };
        saveAttemptMap(map);
        return;
    }

    map[email] = {
        count: current.count + 1,
        firstFailedAt: current.firstFailedAt,
    };
    saveAttemptMap(map);
}

function getLockoutTimeRemaining(email) {
    const map = getAttemptMap();
    const entry = map[email];
    if (!entry) return 0;

    const now = Date.now();
    if (now - entry.firstFailedAt > LOCKOUT_WINDOW_MS) {
        clearFailedAttempts(email);
        return 0;
    }

    if (entry.count < MAX_FAILED_ATTEMPTS) return 0;
    return LOCKOUT_WINDOW_MS - (now - entry.firstFailedAt);
}

function formatRemainingMs(ms) {
    const totalSeconds = Math.max(1, Math.ceil(ms / 1000));
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    if (minutes <= 0) return `${seconds}s`;
    return `${minutes}m ${seconds}s`;
}

async function withTimeout(task, timeoutMs, timeoutMessage) {
    let timerId;
    const timeoutPromise = new Promise((_, reject) => {
        timerId = setTimeout(() => reject(new Error(timeoutMessage)), timeoutMs);
    });

    try {
        return await Promise.race([task, timeoutPromise]);
    } finally {
        clearTimeout(timerId);
    }
}

export default function Login() {

    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [showPass, setShowPass] = useState(false);
    const [resetEmail, setResetEmail] = useState("");
    const [resetLoading, setResetLoading] = useState(false);
    const [resetMessage, setResetMessage] = useState("");

    const loginCardRef = useRef(null);
    const emailInputRef = useRef(null);

    const navigate = useNavigate();

    const handleHeroLoginClick = () => {
        loginCardRef.current?.scrollIntoView({ behavior: "smooth" });
        setTimeout(() => emailInputRef.current?.focus(), 300);
    };

    // 🔥 CLEAN ERROR MESSAGE
    const getFriendlyError = (code) => {
        switch (code) {
            case "auth/user-not-found":
            case "auth/wrong-password":
            case "auth/invalid-credential":
                return "Invalid email or password";
            case "auth/invalid-email":
                return "Invalid email format";
            case "auth/too-many-requests":
                return "Too many attempts. Try again later";
            case "auth/network-request-failed":
                return "Unable to reach server. Check internet and try again";
            case "auth/operation-not-allowed":
                return "Email/password login is disabled in Firebase Auth settings";
            case "auth/invalid-api-key":
                return "Firebase API key is invalid. Check deployment environment config";
            default:
                return "Login failed. Try again";
        }
    };

    const getResetFriendlyError = (code) => {
        switch (code) {
            case "auth/invalid-email":
                return "Please enter a valid email address for reset";
            case "auth/too-many-requests":
                return "Too many reset attempts. Please try again later";
            case "auth/network-request-failed":
                return "Network issue. Please try again";
            default:
                return "Could not send reset email right now";
        }
    };

    const handleLogin = async () => {
        setError("");
        setResetMessage("");

        const normalizedEmail = email.trim().toLowerCase();
        const normalizedPassword = password;

        if (!normalizedEmail || !normalizedPassword) {
            setError("Please fill all fields");
            return;
        }

        if (typeof navigator !== "undefined" && !navigator.onLine) {
            setError("You appear to be offline. Please connect to the internet and try again");
            return;
        }

        const lockRemainingMs = getLockoutTimeRemaining(normalizedEmail);
        if (lockRemainingMs > 0) {
            setError(`Too many failed attempts. Try again in ${formatRemainingMs(lockRemainingMs)}.`);
            return;
        }

        setLoading(true);

        try {
            console.log("🔐 Attempting login for:", normalizedEmail);
            
            const userCredential = await retryAsync(
                () => signInWithEmailAndPassword(auth, normalizedEmail, normalizedPassword),
                2,
                350
            );
            console.log("✅ Auth successful:", userCredential.user.uid);

            const user = userCredential.user;

            // ⚡ FAST REDIRECT - Check minimal data first
            console.log("📊 Fetching user data from database...");
            const userRef = doc(db, "users", user.uid);
            let userData;

            try {
                const userSnap = await getDoc(userRef);

                if (!userSnap.exists()) {
                    console.warn("⚠️ User profile not found. Creating a default profile...");
                    const fallbackName = (user.displayName || normalizedEmail.split("@")[0] || "Customer").trim();
                    const defaultProfile = {
                        name: fallbackName,
                        email: normalizedEmail,
                        role: "user",
                        createdAt: serverTimestamp(),
                        blocked: false,
                        orderCount: 0,
                        online: true,
                    };

                    await withTimeout(
                        setDoc(userRef, defaultProfile, { merge: true }),
                        2200,
                        "profile-write-timeout"
                    );
                    userData = { ...defaultProfile, role: "user", blocked: false };
                    console.log("✅ Default user profile created for login fallback");
                } else {
                    userData = userSnap.data();
                    console.log("✅ User data loaded, role:", userData.role);
                }
            } catch (profileErr) {
                console.error("⚠️ Profile fetch failed, continuing with safe fallback:", profileErr.code, profileErr.message);
                userData = {
                    role: "user",
                    blocked: false,
                };
            }
            
            // ❌ BLOCK CHECK - Fail fast if blocked
            if (userData.blocked) {
                console.warn("⚠️ Account is blocked");
                setError("Your account is blocked");
                await signOut(auth);
                setLoading(false);
                return;
            }

            const session = {
                uid: user.uid,
                email: user.email,
                role: userData.role || "user",
            };

            localStorage.setItem("user", JSON.stringify(session));
            clearFailedAttempts(normalizedEmail);
            console.log("✅ Session saved, redirecting to:", userData.role === "admin" ? "admin-dashboard" : "home");

            // ⚡ INSTANT REDIRECT - No delay
            redirectUser(session, navigate);
            setLoading(false);

        } catch (error) {
            console.error("❌ Login error:", error.code, error.message);
            if (["auth/user-not-found", "auth/wrong-password", "auth/invalid-credential"].includes(error.code)) {
                registerFailedAttempt(normalizedEmail);
            }

            if (error.code === "auth/network-request-failed") {
                const networkCheck = await checkFirebaseAuthReachability();
                if (!networkCheck.ok) {
                    setError(`Auth service unreachable (${networkCheck.reason}). ${getFirebaseAuthTroubleshootingHint()}`);
                } else {
                    setError("Your internet is working, but Firebase auth requests are unstable. Please retry in a moment.");
                }
            } else {
                setError(getFriendlyError(error.code));
            }

            setLoading(false);
        }
    };

    const handleForgotPassword = async () => {
        setError("");
        setResetMessage("");

        const emailToReset = (resetEmail || email).trim().toLowerCase();
        if (!emailToReset) {
            setError("Enter your email first to reset password");
            return;
        }

        setResetLoading(true);
        try {
            await sendPasswordResetEmail(auth, emailToReset);
            setResetMessage("If an account exists for this email, a reset link has been sent.");
        } catch (err) {
            if (err.code === "auth/user-not-found") {
                setResetMessage("If an account exists for this email, a reset link has been sent.");
            } else {
                setError(getResetFriendlyError(err.code));
            }
        } finally {
            setResetLoading(false);
        }
    };

    return (
        <div className="login-container">

            {/* BACKGROUND */}
            <div className="bg-glow"></div>
            <div className="bg-glow2"></div>

            {/* MOBILE BRAND */}
            <div className="mobile-brand">
                <h1>Shreeji Surti Swad</h1>
            </div>

            {/* MOBILE LOGO */}
            <div className="mobile-logo">
                <div className="logo-icon">
                    <img src={logo} alt="logo" />
                </div>
            </div>

            {/* LEFT */}
            <div className="login-left">

                <div className="login-card" ref={loginCardRef}>

                    <h2>Welcome Back 👋</h2>
                    <p>Login to continue ordering</p>

                    {/* ERROR UI 🔥 */}
                    {error && (
                        <div className="error-box">
                            ⚠ {error}
                        </div>
                    )}

                    <form
                        className="auth-form"
                        onSubmit={(e) => {
                            e.preventDefault();
                            handleLogin();
                        }}
                    >
                        {/* EMAIL */}
                        <div className="input-box">
                            <input
                                ref={emailInputRef}
                                type="email"
                                value={email}
                                onChange={(e) => {
                                    setEmail(e.target.value);
                                    setResetEmail(e.target.value);
                                }}
                                placeholder=" "
                                autoComplete="email"
                                required
                            />
                            <label>Email</label>
                        </div>

                        {/* PASSWORD */}
                        <div className="input-box password-box">
                            <input
                                type={showPass ? "text" : "password"}
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                placeholder=" "
                                autoComplete="current-password"
                                required
                            />
                            <label>Password</label>

                            <span
                                className="toggle-pass"
                                onClick={() => setShowPass(!showPass)}
                            >
                                {showPass ? "🙈" : "👁️"}
                            </span>
                        </div>

                        {resetMessage && (
                            <div className="info-box">{resetMessage}</div>
                        )}

                        <button
                            type="button"
                            className="forgot-link"
                            onClick={handleForgotPassword}
                            disabled={resetLoading}
                        >
                            {resetLoading ? "Sending reset link..." : "Forgot Password?"}
                        </button>

                        {/* LOGIN BUTTON */}
                        <button
                            type="submit"
                            className={`shine-btn ${loading ? "loading" : ""}`}
                            disabled={loading}
                        >
                            {loading ? "Logging in..." : "Login →"}
                        </button>

                        {/* REGISTER */}
                        <button
                            type="button"
                            className="create"
                            onClick={() => navigate("/register")}
                        >
                            Create Account
                        </button>
                    </form>

                </div>

            </div>

            {/* RIGHT */}
            <div
                className="login-right"
                onClick={handleHeroLoginClick}
                style={{ "--hero-image": `url(${heroImage})` }}
            >

                <div className="badge badge1">🔥 Fresh Food</div>
                <div className="badge badge2">🚀 Fast Delivery</div>

                <div className="overlay-text">
                    <h2>Surti Experience</h2>
                    <p>Feel the taste before you order</p>
                </div>

            </div>

        </div>
    );
}